import { FLAT_MARK } from "./flatMark";
import { goldAt, paletteAt, smooth } from "./reveal";

// PROTOTYPE (hero shapes): where Costellazione's clients, AVRAH's sphere and
// the count are at each point of its story (DiscTransitions.jsx), told in
// two breaths (breaths.js). Positions are in the disc's box, in pixels,
// as the disc layout's are (dotShapes.js).
//
// First the disc fills: the clients stay where they are and turn gold as
// AVRAH reaches them (reveal.js), the count climbing with them. Then the
// circle goes on into AVRAH's mark (markTargets.js): its clients come off
// it, each gold from when it takes off, and draw the mark in two strokes
// (STROKES). Before the clients land round the count, in the middle, it
// shrinks to its size on the sphere, AVRAH's ball (paintBall.js), which
// grows in under it.

const clamp01 = (value) => Math.min(1, Math.max(0, value));
const mix = (a, b, t) => a + (b - a) * t;
const TURN = Math.PI * 2;

// How much of the disc's side the mark takes at most.
const MARK_SPAN = 0.96;
// How much of the spacing a client's dot takes across on the mark, more than
// on the disc, so the mark reads solid: no two places are nearer than 0.8
// of it (markTargets.js), so they never touch; and how big it is at most, in
// pixels.
const ON_MARK = { dot: 0.72, most: 24 };
// At how many points along a client's flight it is checked for coming
// under the count.
const ALONG = 96;
// How a client springs off the circle onto the mark: over `flight` of the
// second breath, pulled to its place by a spring that settles at `rate` (in
// radians per flight) without bouncing, critically damped; launched along
// the circle, round the middle, at `swirl` of the speed the spring would
// first pull it at; as big as on the mark by `grown` of its flight, and as
// it flies over the others, `lift` bigger at most, back to its size by
// `lifted` of its flight.
const SPRING = { flight: 0.42, rate: 9, swirl: 0.5, grown: 0.625, lift: 0.1, lifted: 0.7 };
// The digits' height, as a share of the count's line; the room the count
// keeps round them, as a share of their height; and on the sphere, set flat
// on it, how far they reach either side of its middle at most, and how tall
// they are at most, in the sphere's radii, well inside its edge.
const DIGITS = 0.72;
const ROOM = 0.15;
const IN_SPHERE = { reach: 0.62, tall: 0.46 };
// How long, as a share of the second breath, the count takes to shrink to
// its size on the sphere; and the sphere then to grow in round it.
const SEAL = 0.2;
// The tones a client can be in, as DotCanvas.jsx paints them.
const TONES = { met: "met", waiting: "waiting", lit: "lit" };

// How the circle draws the mark, in strokes, the way Apple's interfaces
// move: the Λ, then the ribbon round the ball, the pen going up both sides
// of each at once, the Λ from its feet to its arch and the ribbon from its
// V to its ends. The clients on the disc's outer ring, as many as the Λ has
// places, draw the Λ; the rest, on its inner ring, draw the ribbon. A ring's
// clients set off one after another, evenly over `at` of the second
// breath, each landing on the next place the pen reaches, each springing
// off along the circle the way the pen goes round the middle (SPRING), and
// flying over the clients at rest (`above`, DotCanvas.jsx).
const STROKES = [
  { part: "lambda", at: [0, 0.32] },
  { part: "ribbon", at: [0.24, 0.56] },
];

// Where the mark goes in the disc's box, in pixels per mark unit and its
// top left: with its sphere on the box's middle, as large as fits.
function markPlacement(side) {
  const { width, height, sphere } = FLAT_MARK;
  const farthest = Math.max(sphere.cx, width - sphere.cx, sphere.cy, height - sphere.cy);
  const scale = (MARK_SPAN * side) / 2 / farthest;
  return { scale, left: side / 2 - sphere.cx * scale, top: side / 2 - sphere.cy * scale };
}

// A point's angle round the `middle`, clockwise from straight up; how far
// clockwise, 0 to a turn, from angle `a` to angle `b`; and how far, and
// which way, from `a` to `b` the shorter way round: clockwise, or back if
// less than 0.
const angleRound = ({ x, y }, middle) => Math.atan2(x - middle, middle - y);
const clockwise = (a, b) => (((b - a) % TURN) + TURN) % TURN;
const nearer = (a, b) => ((clockwise(a, b) + TURN / 2) % TURN) - TURN / 2;

// How the disc's clients draw the mark, the count's box `count` (in
// pixels) as it is laid out, on the mark's places for as many clients
// (`targets`, from markTargets.js): the disc's dots in the order the plan takes
// them, the place on the mark each lands on, and each one's rank on the
// disc, the order it fills in; when each sets off, as a share of the second
// breath; how each springs; how much each client grows to be as big as the
// mark's spacing has it; the sphere, and how much the count shrinks to be
// set on it.
//
// No client rests under the count. It has shrunk into the sphere's room by
// `sealsAt` (in the second breath): as the first client to come under it,
// in the middle (its way), sets off, but no sooner than SEAL into the
// breath, so it never shrinks quicker, and no later than SEAL before its
// end, so the sphere is whole as the breath ends. A client comes under it
// where it lands or on its way; a client flying under it before it has
// shrunk makes way for it (constellationFrame).
export function constellationPlan(disc, { count, targets }) {
  const side = disc.width;
  const middle = side / 2;
  const clients = disc.dots.length;
  const { points, pitch } = targets;
  if (points.length !== clients) throw new Error(`Costellazione: ${points.length} places on the mark for ${clients} clients`);
  const { scale, left, top } = markPlacement(side);
  const size = Math.min(ON_MARK.dot * pitch * scale, ON_MARK.most);
  const digits = DIGITS * count.height;
  // Whether a client's dot on the mark, at a point, shows under the count or
  // in the room it keeps.
  const reach = size / 2 + ROOM * digits;
  const inWay = ({ x, y }) => Math.abs(x - middle) < count.width / 2 + reach && Math.abs(y - middle) < digits / 2 + reach;
  const marked = points.map(({ x, y, part }) => ({ x: left + x * scale, y: top + y * scale, part }));
  const flights = inStrokes(disc.dots, marked, middle);
  const ranks = flights.map(({ rank }) => rank);
  const from = ranks.map((rank) => disc.dots[rank]);
  const to = flights.map(({ to: { x, y } }) => ({ x, y }));
  const departures = flights.map(({ departure }) => departure);
  const springs = flights.map(({ sense }, i) => springOf(from[i], to[i], sense, middle));
  const comesUnder = (i) => Array.from({ length: ALONG + 1 }, (_, k) => sprung(springs[i], to[i], 1, k / ALONG)).some(inWay);
  const underCount = to.map((_, i) => i).filter(comesUnder);
  const clearsAt = underCount.length ? Math.min(...underCount.map((i) => departures[i])) : Math.max(...departures);
  const sealsAt = Math.min(Math.max(clearsAt, SEAL), 1 - SEAL);
  if (underCount.some((i) => inWay(to[i]) && departures[i] + SPRING.flight <= sealsAt)) throw new Error(`Costellazione: with ${clients} clients, one lands under the count before it has shrunk`);
  const sphere = { x: left + FLAT_MARK.sphere.cx * scale, y: top + FLAT_MARK.sphere.cy * scale, r: FLAT_MARK.sphere.r * scale };
  const fit = Math.min((2 * IN_SPHERE.reach * sphere.r) / count.width, (IN_SPHERE.tall * sphere.r) / digits);
  return {
    from,
    ranks,
    to,
    departures,
    springs,
    grows: size / disc.size,
    layout: { ...disc, dots: from },
    sphere,
    fit,
    digits: { width: count.width, height: digits },
    middle,
    sealsAt,
  };
}

// How the disc's `dots` draw the `marked` places, stroke after stroke
// (STROKES): the rank of each, the place it lands on, when it sets off, and
// which way round the `middle` it sets off, 1 clockwise or -1. Each
// stroke's places as the pen would go once round the middle clockwise: the
// Λ's as markTargets gives them, the ribbon's the other way.
function inStrokes(dots, marked, middle) {
  const angleOf = (point) => angleRound(point, middle);
  const round = {
    lambda: marked.filter(({ part }) => part === "lambda"),
    ribbon: marked.filter(({ part }) => part === "ribbon").reverse(),
  };
  const all = dots.map((_, rank) => rank);
  const ring = { lambda: all.slice(dots.length - round.lambda.length), ribbon: all.slice(0, round.ribbon.length) };
  return STROKES.flatMap(({ part, at }) => {
    const [up, down] = sidesOf(round[part], part);
    // The ring's clients, going round it clockwise, take the places the
    // pen goes up and then, from the top, those it goes down.
    const taken = evenly(ring[part], [...up, ...down.slice().reverse()], (rank) => angleOf(dots[rank]), angleOf);
    const inTurn = (ranks, places, sense) =>
      ranks.map((rank, k) => ({
        rank,
        to: places[k],
        departure: ranks.length > 1 ? mix(at[0], at[1], k / (ranks.length - 1)) : at[0],
        sense,
      }));
    return [...inTurn(taken.slice(0, up.length), up, 1), ...inTurn(taken.slice(up.length).reverse(), down, -1)];
  });
}

// A stroke's places as the pen draws it up both sides at once, from those
// as it goes once round the middle clockwise (`round`): the side it draws
// clockwise, up the left, and the side it draws the other way, up the
// right, each from the bottom up. The Λ rises from its feet to its arch,
// its highest place; the ribbon from its V, its lowest, to its ends.
function sidesOf(round, part) {
  const heights = round.map(({ y }) => y);
  if (part === "lambda") {
    const arch = heights.indexOf(Math.min(...heights));
    return [round.slice(0, arch + 1), round.slice(arch + 1).reverse()];
  }
  const v = heights.indexOf(Math.max(...heights));
  return [round.slice(v), round.slice(0, v).reverse()];
}

// The `ranks` of a ring's clients in the order they take `places`, which
// go once round the middle clockwise: going round it clockwise from where,
// were the clients spread evenly round it, the farthest any is from its
// place, round the middle, is least. `angleOf` a rank, or a place, is its
// angle round the middle.
function evenly(ranks, places, angleOfRank, angleOf) {
  let unwound = angleOf(places[0]);
  const starts = places.map((place, k) => {
    if (k > 0) unwound += nearer(angleOf(places[k - 1]), angleOf(place));
    return unwound - (TURN * k) / places.length;
  });
  const start = (Math.min(...starts) + Math.max(...starts)) / 2;
  return ranks
    .map((rank) => [clockwise(start, angleOfRank(rank)), rank])
    .sort((a, b) => a[0] - b[0])
    .map(([, rank]) => rank);
}

// How a client at `a` springs to its place `b`: how far it starts off its
// place, and how fast it is launched (per flight), along the circle, round
// the `middle`, clockwise if `sense` is 1 or the other way if -1.
function springOf(a, b, sense, middle) {
  const off = [a.x - b.x, a.y - b.y];
  const speed = sense * SPRING.swirl * SPRING.rate * Math.hypot(off[0], off[1]);
  const angle = angleRound(a, middle);
  return { off, launch: [speed * Math.cos(angle), speed * Math.sin(angle)] };
}

// How far the second breath has come, 0 to 1.
function morphOf(acts) {
  if (!Number.isFinite(acts.morph)) throw new Error(`Costellazione: the story needs how far its second breath has come, not ${acts.morph}`);
  return clamp01(acts.morph);
}

// How far the `i`th client in the plan has flown, 0 to 1: all of them have
// landed as the second breath ends, as their timing has it, but for how the
// sums round.
export function flightOf(plan, i, acts) {
  const morph = morphOf(acts);
  return morph === 1 ? 1 : clamp01((morph - plan.departures[i]) / SPRING.flight);
}

// Each client where its flight has it, as much as it has grown, in its
// colour, a tone DotCanvas.jsx paints: until it takes off, as the disc has
// it as it fills (reveal.js), then "lit"; and whether it is flying over the
// others (`above`). Where the count or the sphere is over a client, the
// client makes way, shrinking to keep the room the count keeps round its
// digits, and out of the sphere, and grows back once it is clear.
export function constellationFrame(plan, acts) {
  const { from, ranks, to, springs, grows, layout, sphere, digits, middle } = plan;
  const at = numberAt(plan, acts);
  const count = { x: middle, y: middle, halfWidth: (at.scale * digits.width) / 2, halfHeight: (at.scale * digits.height) / 2 };
  const room = ROOM * at.scale * digits.height;
  const resting = paletteAt({ met: acts.met, gold: goldAt({ clients: from.length, met: acts.met }, acts.fill) }, TONES);
  const dots = from.map((_, i) => {
    const flown = flightOf(plan, i, acts);
    const { x, y, k } = sprung(springs[i], to[i], grows, flown);
    const clear = Math.min(
      Math.hypot(Math.max(0, Math.abs(x - count.x) - count.halfWidth), Math.max(0, Math.abs(y - count.y) - count.halfHeight)) - room,
      at.sphere > 0 ? Math.hypot(x - sphere.x, y - sphere.y) - at.sphere : Infinity,
    );
    return {
      x,
      y,
      k: Math.min(k, Math.max(0, (2 * clear) / layout.size)),
      tone: flown > 0 ? TONES.lit : resting(ranks[i]),
      above: flown > 0 && flown < 1,
    };
  });
  return { ...layout, dots };
}

// Where a spring flight to `b` is, `t` (0 to 1) of the way, and how big,
// growing to `grows` times its size (SPRING). Where the spring alone would
// leave it short of its place as the flight ends, it is drawn on to it, by
// `t` cubed of that, so it lands.
function sprung({ off, launch }, b, grows, t) {
  const { rate } = SPRING;
  const left = (s) => off.map((d, k) => (d + (launch[k] + rate * d) * s) * Math.exp(-rate * s));
  const short = left(1);
  const [dx, dy] = left(t).map((d, k) => d - short[k] * t ** 3);
  const lifted = 1 + SPRING.lift * Math.sin(Math.PI * Math.min(1, t / SPRING.lifted));
  return { x: b.x + dx, y: b.y + dy, k: mix(1, grows, smooth(Math.min(1, t / SPRING.grown))) * lifted };
}

// How big the count is, in the box's middle, and how big the sphere is, its
// radius in pixels, 0 until it is there. The count stays as big as it
// starts while the disc fills and the mark is drawn round it; over SEAL of
// the second breath until `sealsAt`, it shrinks to its size on the sphere,
// and no smaller; then the sphere grows in from its middle, under the
// count, which is set on it in dark ink where it lies on it
// (paintCount.js), so no gold digit lies on the gold sphere.
export function numberAt({ sphere, fit, sealsAt }, acts) {
  const morph = morphOf(acts);
  const shrunk = smooth(clamp01((morph - sealsAt + SEAL) / SEAL));
  const grown = smooth(clamp01((morph - sealsAt) / SEAL));
  return { scale: mix(1, fit, shrunk), sphere: sphere.r * grown };
}
